import { createSlice, createSelector } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { MW_5_15_0 } from '../../constants/migrationVersions';
import { runMigration } from '../../migrations';

const initialState = {
  requests: { calls: {}, nonCalls: {} },
  removedAirAmbulances: { calls: {}, nonCalls: {} }, // removed rescues
  selectedAirAmbulance: null,
  formPageToShow: null,
  hoveredAirAmbulance: null,
  temporaryLocation: null, // hovered and selected address for TempLocationGL (should probably move to a new slice)
  tentativeETALocation: null, // the location to fetch ETA for
  isNonCallSubmit: false,
  paneOpen: false, // set whether the pane should be open or not
  preselectedLandingZone: null, // landing zone selected from map marker
};

// Selector to get existing request
export const selectExistingRequest = createSelector(
  [
    state => state.airAmbulance.requests,
    state => state.emergencyCalls.selectedCallerID,
    state => state.airAmbulance.selectedAirAmbulance,
  ],
  (requests, selectedCallerID, selectedAirAmbulance) => {
    return (
      (requests?.calls && requests.calls[selectedCallerID]) ||
      (requests?.nonCalls &&
        requests.nonCalls[selectedAirAmbulance?.airAmbulanceID]) ||
      null
    );
  },
);

const airAmbulanceSlice = createSlice({
  name: 'airAmbulance',
  initialState,
  reducers: {
    setIsNonCallSubmit: (state, action) => {
      state.isNonCallSubmit = action.payload;
    },
    setPaneOpen: (state, action) => {
      state.paneOpen = action.payload;
    },
    setSelectedAirAmbulance: (state, action) => {
      const airAmbulanceID = action.payload;
      state.selectedAirAmbulance = {
        ...state.requests.nonCalls[airAmbulanceID],
        airAmbulanceID,
      };
    },
    removeAirAmbulanceRequest: (state, action) => {
      const airAmbulanceID = action.payload;
      const isCall = state.requests.calls[airAmbulanceID];
      if (isCall) {
        state.removedAirAmbulances[airAmbulanceID] =
          state.requests.calls[airAmbulanceID];
        delete state.requests.calls[airAmbulanceID];
      } else {
        state.removedAirAmbulances[airAmbulanceID] =
          state.requests.nonCalls[airAmbulanceID];
        delete state.requests.nonCalls[airAmbulanceID];
      }
    },
    resetSelectedAirAmbulance: state => {
      return {
        ...state,
        selectedAirAmbulance: null,
      };
    },
    setPreselectedLandingZone: (state, action) => {
      return {
        ...state,
        preselectedLandingZone: action.payload,
      };
    },
    resetPreselectedLandingZone: state => {
      return {
        ...state,
        preselectedLandingZone: null,
      };
    },
    setHoveredAirAmbulance: (state, action) => {
      return {
        ...state,
        hoveredAirAmbulance: action.payload,
      };
    },
    setTemporaryLocation: (state, action) => {
      return {
        ...state,
        temporaryLocation: action.payload,
      };
    },
    setTentativeETALocation: (state, action) => {
      return {
        ...state,
        tentativeETALocation: action.payload,
      };
    },
    resetHoveredAirAmbulance: state => {
      return {
        ...state,
        hoveredAirAmbulance: null,
      };
    },
    addAndUpdateAirAmbulanceRequest: (state, action) => {
      const message = action.payload?.message;
      const hasMostRecentLocation = action.payload?.hasMostRecentLocation;
      // Check if it already exists in inactive
      const isCallPayload = message?.topic.includes('tel');
      const airAmbulanceID = message?.topic.split('//')[1];
      if (isCallPayload) {
        state.requests = {
          ...state.requests,
          calls: { ...state.requests.calls, [airAmbulanceID]: message },
          formPageToShow: 'air_ambulance_status',
        };
      } else {
        const airAmbulanceWithId = { ...message, airAmbulanceID };
        const shouldSelectAirAmbulance =
          (!hasMostRecentLocation && state.isNonCallSubmit) || state.paneOpen;

        return {
          ...state,
          selectedAirAmbulance: shouldSelectAirAmbulance
            ? airAmbulanceWithId
            : null,
          requests: {
            ...state.requests,
            nonCalls: {
              ...state.requests.nonCalls,
              [airAmbulanceID]: message,
            },
          },
          formPageToShow: 'air_ambulance_status', // if request is non-call we can safely assume it's from the action bar while in all view
          isNonCallSubmit: false, // reset the isNonCallSubmit to false after the request is submitted
        };
      }
    },
    removeAllNonCallEvents: state => {
      const newState = {
        ...state,
        requests: { ...state.requests, nonCalls: {} },
        selectedAirAmbulance: null,
        removedAirAmbulances: {
          ...state.removedAirAmbulances,
          nonCalls: {
            ...state.removedAirAmbulances.nonCalls,
            ...state.requests.nonCalls,
          },
        },
      };

      return newState;
    },
    /**
     * Updates the status of an air ambulance request for a given phone number.
     * @param {Object} state - The current Redux state.
     * @param {Object} action - The dispatched action containing the payload.
     * @param {string} action.payload.phone - The phone number associated with the request.
     * @param {string} action.payload.status - The new status to update for the request.
     */
    updateAirAmbulanceRequestStatus: (state, action) => {
      const { phone, status } = action.payload;
      const requestToUpdate = state.requests[phone];
      if (requestToUpdate) {
        requestToUpdate.status = status;
      }
    },

    setFormPageToShow: (state, action) => {
      state.formPageToShow = action.payload;
    },
    extraReducers: builder => {
      builder.addCase('APP_INIT_START', state => {
        const migrationList = [MW_5_15_0];
        let newState = { ...state };
        migrationList.forEach(migration => {
          newState = runMigration(newState, 'airAmbulance', migration);
        });
        return newState;
      });
    },
  },
});

export const {
  setPaneOpen,
  setIsNonCallSubmit,
  setSelectedAirAmbulance,
  resetSelectedAirAmbulance,
  setPreselectedLandingZone,
  resetPreselectedLandingZone,
  addAndUpdateAirAmbulanceRequest,
  updateAirAmbulanceRequestStatus,
  removeAirAmbulanceRequest,
  setFormPageToShow,
  removeAllNonCallEvents,
  setHoveredAirAmbulance,
  resetHoveredAirAmbulance,
  setTemporaryLocation,
  setTentativeETALocation,
} = airAmbulanceSlice.actions;

const persistConfig = {
  key: 'airAmbulance',
  storage,
  blacklist: ['temporaryLocation', 'tentativeETALocation'],
};

const persistedReducer = persistReducer(
  persistConfig,
  airAmbulanceSlice.reducer,
);

export default persistedReducer;
